/**
 * Online booking contracts (WP3-BOOKING, BUILD-PLAN M14; research
 * online-booking.md section 4). Imported as `@velora/contracts/booking`.
 *
 * One booking engine, several front doors (online-booking 4.1): the public
 * patient app (`apps/booking-web`), DentChat, the voice receptionist and the
 * staff preview all read the same availability. Two route families:
 *
 *   Public  `/v1/booking/public/*`  no session, rate limited, no PHI out
 *           except what the visitor typed or an identity token unlocked.
 *   Staff   `/v1/booking/*`          office scoped, `schedule.view` to read,
 *           `schedule.edit` to approve, reject, link or change settings.
 *
 * Times are ISO instants with offset; dates are office-local `YYYY-MM-DD`.
 */
import { z } from 'zod';
import { TurnstilePublicSchema, TurnstileTokenSchema } from './turnstile.js';

const LocalDateSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'expected YYYY-MM-DD');
const InstantSchema = z.iso.datetime({ offset: true });
const SlugSchema = z
  .string()
  .regex(/^[a-z0-9][a-z0-9-]{2,62}$/, 'lowercase letters, digits and dashes, 3 to 63 characters');

/** The deterministic "creator" of online bookings (OB-12). Never a staff user. */
export const ONLINE_BOOKING_ACTOR = {
  /** Stable id used where a column needs a uuid; no user row has it. */
  userId: '00000000-0000-4000-8000-00000000b001',
  name: 'Online booking (web)',
} as const;

/** Hold length (OB-03). */
export const HOLD_SECONDS = 5 * 60;
/** A failed write keeps the slot leased this long (OB-14). */
export const WRITE_FAILED_LEASE_MINUTES = 30;
/** Abandoned visits become a lead after this long (OB-19). */
export const ABANDON_AFTER_MINUTES = 10;
/** Identity link lifetime (OB-09). */
export const LINK_TOKEN_DAYS = 14;
/** Emergency callback SLA (OB-10). */
export const EMERGENCY_SLA_MINUTES = 15;

/* ------------------------------------------------------------------------ */
/* Rules (per office, per appointment type)                                  */
/* ------------------------------------------------------------------------ */

export const BookingAudienceSchema = z.enum(['new', 'existing', 'both']);
export type BookingAudience = z.infer<typeof BookingAudienceSchema>;

/** `auto` requires Perfect Day blocks only when the office has any for the type. */
export const BookingBlockModeSchema = z.enum(['auto', 'required', 'ignore']);
export type BookingBlockMode = z.infer<typeof BookingBlockModeSchema>;

/** Approve-first is forced while the office cannot write appointments (BUILD-PLAN 2.5.3). */
export const BookingWriteModeSchema = z.enum(['auto_confirm', 'approve_first']);
export type BookingWriteMode = z.infer<typeof BookingWriteModeSchema>;

export const BookingAgeBandSchema = z.enum(['all', 'adult', 'child']);
export type BookingAgeBand = z.infer<typeof BookingAgeBandSchema>;

export const BOOKING_STEP_MINUTES = [5, 10, 15, 20, 30, 60] as const;

export const BookingTypeRuleSchema = z.object({
  appointmentTypeId: z.uuid(),
  /** Bookable online at this office. */
  enabled: z.boolean(),
  audience: BookingAudienceSchema,
  /** Patient-facing name; the internal type name is never shown to patients. */
  patientLabel: z.string().min(1).max(60),
  description: z.string().max(240).default(''),
  minNoticeMin: z
    .number()
    .int()
    .min(0)
    .max(14 * 24 * 60),
  horizonDays: z.number().int().min(1).max(180),
  stepMin: z.union(BOOKING_STEP_MINUTES.map((n) => z.literal(n))),
  /** Per provider per day; null means no cap. */
  dailyCapPerProvider: z.number().int().min(1).max(50).nullable(),
  /** Empty means every provider scheduled in an eligible operatory. */
  providerIds: z.array(z.uuid()).max(50),
  /** Empty means operatories marked online-bookable, or with a block for this type. */
  operatoryIds: z.array(z.uuid()).max(100),
  blockMode: BookingBlockModeSchema,
  /** Emergency mode: today and tomorrow only, soonest first, call and callback fallback. */
  urgent: z.boolean(),
  writeMode: BookingWriteModeSchema,
  ageBand: BookingAgeBandSchema,
  childMaxAge: z.number().int().min(1).max(25),
});
export type BookingTypeRule = z.infer<typeof BookingTypeRuleSchema>;

export const BookingTypeRuleInputSchema = BookingTypeRuleSchema.omit({ appointmentTypeId: true });
export type BookingTypeRuleInput = z.infer<typeof BookingTypeRuleInputSchema>;

export const BookingSiteSchema = z.object({
  locationId: z.uuid(),
  slug: SlugSchema,
  enabled: z.boolean(),
  headline: z.string().max(80),
  intro: z.string().max(400),
  /** Minutes a new request may wait before it is late in the queue. */
  slaMinutes: z
    .number()
    .int()
    .min(5)
    .max(24 * 60),
});
export type BookingSite = z.infer<typeof BookingSiteSchema>;

export const BookingSiteInputSchema = BookingSiteSchema.omit({ locationId: true });
export type BookingSiteInput = z.infer<typeof BookingSiteInputSchema>;

/** How a confirmed booking reaches the PMS at this office. */
export const BookingWritePathSchema = z.enum([
  /** Velora owns appointments here (no PMS, or the class migrated): written at once. */
  'direct',
  /** A verified `appointment.create` command goes to the outbox. */
  'command',
  /** The PMS cannot take the write yet: staff enter it in the PMS and mark it entered. */
  'manual',
]);
export type BookingWritePath = z.infer<typeof BookingWritePathSchema>;

export const BookingWritePathInfoSchema = z.object({
  path: BookingWritePathSchema,
  /** Why the path is not `command` or `direct`, shown verbatim. */
  reason: z.string().nullable(),
  pmsSystem: z.enum(['opendental', 'dentrix', 'eaglesoft']).nullable(),
});
export type BookingWritePathInfo = z.infer<typeof BookingWritePathInfoSchema>;

/** Staff settings view for one office. */
export const BookingSettingsSchema = z.object({
  locationId: z.uuid(),
  officeName: z.string(),
  site: BookingSiteSchema.nullable(),
  /** Suggested slug when no site exists yet. */
  suggestedSlug: SlugSchema,
  /** The public page for this office (`<VELORA_BOOKING_PUBLIC_URL>/b/<slug>`), null without a site. */
  publicUrl: z.string().nullable(),
  writePath: BookingWritePathInfoSchema,
  types: z.array(
    z.object({
      appointmentTypeId: z.uuid(),
      name: z.string(),
      lengthMin: z.number().int(),
      colorToken: z.string(),
      timePattern: z.string().nullable(),
      /** True when the office has online Perfect Day blocks for this type. */
      hasBlocks: z.boolean(),
      /** True when the values are defaults (no saved row yet). */
      isDefault: z.boolean(),
      rule: BookingTypeRuleSchema,
    }),
  ),
  providers: z.array(
    z.object({ id: z.uuid(), name: z.string(), kind: z.string(), colorToken: z.string() }),
  ),
  operatories: z.array(
    z.object({
      id: z.uuid(),
      name: z.string(),
      isHygiene: z.boolean(),
      onlineBookable: z.boolean(),
    }),
  ),
});
export type BookingSettings = z.infer<typeof BookingSettingsSchema>;

/* ------------------------------------------------------------------------ */
/* Public: site profile                                                      */
/* ------------------------------------------------------------------------ */

export const PublicTypeSchema = z.object({
  id: z.uuid(),
  label: z.string(),
  description: z.string(),
  lengthMin: z.number().int(),
  audience: BookingAudienceSchema,
  urgent: z.boolean(),
  ageBand: BookingAgeBandSchema,
});
export type PublicType = z.infer<typeof PublicTypeSchema>;

export const PublicProviderSchema = z.object({
  id: z.uuid(),
  /** "Dr. Avery Lane" or "Jamie Cole, RDH". */
  displayName: z.string(),
  credential: z.string(),
  initials: z.string(),
  kind: z.enum(['dentist', 'hygienist', 'other']),
  colorToken: z.string(),
});
export type PublicProvider = z.infer<typeof PublicProviderSchema>;

/** The SMS opt-in wording the booking page shows (server-rendered, versioned). */
export const SmsConsentWordingSchema = z.object({
  version: z.string(),
  transactional: z.object({ label: z.string(), text: z.string() }),
  marketing: z.object({ label: z.string(), text: z.string() }),
  termsUrl: z.string(),
  privacyUrl: z.string(),
});
export type SmsConsentWording = z.infer<typeof SmsConsentWordingSchema>;

export const PublicSiteSchema = z.object({
  slug: SlugSchema,
  practiceName: z.string(),
  officeName: z.string(),
  phone: z.string().nullable(),
  address: z.object({
    line1: z.string().nullable(),
    city: z.string().nullable(),
    state: z.string().nullable(),
    postalCode: z.string().nullable(),
  }),
  timezone: z.string(),
  headline: z.string(),
  intro: z.string(),
  /** Office-local today. */
  today: LocalDateSchema,
  /** Types visible to this visitor (existing-only types need an identity token, OB-07). */
  types: z.array(PublicTypeSchema),
  providers: z.array(PublicProviderSchema),
  /** Present when the visitor carries a valid identity token (OB-09). */
  identity: z
    .object({
      firstName: z.string(),
      lastName: z.string(),
      birthdate: LocalDateSchema.nullable(),
      mobileMasked: z.string().nullable(),
      emailMasked: z.string().nullable(),
      /** Online booking is blocked for this patient: show "please call us" (OB-17). */
      mustCall: z.boolean(),
    })
    .nullable(),
  /** Bot check for this page's forms; absent or `siteKey: null` renders nothing. */
  turnstile: TurnstilePublicSchema.optional(),
  /** SMS opt-in wording (A2P 10DLC); the booking request echoes its version. */
  smsConsent: SmsConsentWordingSchema.optional(),
});
export type PublicSite = z.infer<typeof PublicSiteSchema>;

/* ------------------------------------------------------------------------ */
/* Availability (the one engine)                                             */
/* ------------------------------------------------------------------------ */

export const AvailabilityQuerySchema = z.object({
  typeId: z.uuid(),
  from: LocalDateSchema.optional(),
  days: z.coerce.number().int().min(1).max(14).default(3),
  providerId: z.uuid().optional(),
  /** Back-to-back family booking (OB-20): the visit just booked. */
  nearStart: InstantSchema.optional(),
  nearEnd: InstantSchema.optional(),
  nearProviderId: z.uuid().optional(),
});
export type AvailabilityQuery = z.infer<typeof AvailabilityQuerySchema>;

export const SlotSchema = z.object({
  start: InstantSchema,
  end: InstantSchema,
  providerId: z.uuid(),
});
export type Slot = z.infer<typeof SlotSchema>;

export const DaySlotsSchema = z.object({
  date: LocalDateSchema,
  slots: z.array(SlotSchema),
});
export type DaySlots = z.infer<typeof DaySlotsSchema>;

export const AvailabilitySchema = z.object({
  typeId: z.uuid(),
  timezone: z.string(),
  dates: z.array(LocalDateSchema),
  /** "First available with anyone": earliest start per time across providers. */
  anyone: z.array(DaySlotsSchema),
  providers: z.array(
    z.object({
      providerId: z.uuid(),
      days: z.array(DaySlotsSchema),
      /** First slot after the window when the window is empty for this provider. */
      nextAvailable: SlotSchema.nullable(),
    }),
  ),
  firstAvailable: SlotSchema.nullable(),
  urgent: z.boolean(),
  /** Back-to-back suggestions for a family member (OB-20), best first. */
  suggestions: z.array(SlotSchema.extend({ reason: z.string() })),
  /** Earliest bookable date and the last date inside the horizon. */
  earliest: LocalDateSchema,
  latest: LocalDateSchema,
  generatedAt: InstantSchema,
});
export type Availability = z.infer<typeof AvailabilitySchema>;

/* ------------------------------------------------------------------------ */
/* Holds                                                                     */
/* ------------------------------------------------------------------------ */

const VisitorIdSchema = z.string().regex(/^[A-Za-z0-9_-]{16,64}$/);

export const HoldRequestSchema = z.object({
  typeId: z.uuid(),
  /** Null books with anyone: the engine picks the provider for that time. */
  providerId: z.uuid().nullable(),
  start: InstantSchema,
  visitorId: VisitorIdSchema,
  /** The visitor's previous hold, released when the new one is taken. */
  replace: z.object({ holdId: z.uuid(), holdToken: z.string().min(20).max(128) }).optional(),
});
export type HoldRequest = z.infer<typeof HoldRequestSchema>;

export const HoldSchema = z.object({
  holdId: z.uuid(),
  holdToken: z.string(),
  expiresAt: InstantSchema,
  start: InstantSchema,
  end: InstantSchema,
  providerId: z.uuid(),
  typeId: z.uuid(),
});
export type Hold = z.infer<typeof HoldSchema>;

export const ReleaseHoldSchema = z.object({ holdToken: z.string().min(20).max(128) });

/** 409 `conflict` details when a slot was taken (OB-04). */
export const SlotTakenDetailsSchema = z.object({
  reason: z.literal('slot_unavailable'),
  alternatives: z.array(SlotSchema).max(3),
});
export type SlotTakenDetails = z.infer<typeof SlotTakenDetailsSchema>;

/* ------------------------------------------------------------------------ */
/* Submitting a request                                                      */
/* ------------------------------------------------------------------------ */

const NameSchema = z.string().trim().min(1).max(60);
const MobileSchema = z
  .string()
  .trim()
  .refine(
    (v) => /^\D*1?\D*([2-9]\d{2})\D*(\d{3})\D*(\d{4})\D*$/.test(v),
    'enter a 10-digit mobile number',
  );

export const AttributionSchema = z.object({
  source: z.string().max(80).optional(),
  medium: z.string().max(80).optional(),
  campaign: z.string().max(120).optional(),
  term: z.string().max(120).optional(),
  content: z.string().max(120).optional(),
  /** Link or channel that brought the visitor: web, google, chat, voice, sms_link, qr. */
  channel: z.enum(['web', 'google', 'chat', 'voice', 'sms_link', 'qr', 'email']).optional(),
  referrer: z.string().max(300).optional(),
  /** Answer to "How did you hear about us?" (asked only when the rest is empty). */
  heardAbout: z.string().max(80).optional(),
});
export type Attribution = z.infer<typeof AttributionSchema>;

export const PersonSchema = z.object({
  firstName: NameSchema,
  lastName: NameSchema,
  birthdate: LocalDateSchema,
  mobile: MobileSchema,
  email: z.email().max(200).optional(),
});
export type Person = z.infer<typeof PersonSchema>;

export const BookingForSchema = z.enum(['self', 'dependent', 'other']);
export type BookingFor = z.infer<typeof BookingForSchema>;

export const SubmitRequestSchema = z.object({
  holdId: z.uuid(),
  holdToken: z.string().min(20).max(128),
  visitorId: VisitorIdSchema,
  bookingFor: BookingForSchema,
  /** The patient. For an identified existing patient the details may be omitted. */
  patient: PersonSchema.partial({ email: true }).optional(),
  /** The person booking, when not the patient (parent, carer). */
  booker: PersonSchema.omit({ birthdate: true }).optional(),
  insurance: z
    .object({ carrier: z.string().max(80), selfPay: z.boolean().default(false) })
    .optional(),
  note: z.string().max(500).optional(),
  /** Both start unchecked (OB-18). `disclosureVersion`: the wording shown (site.smsConsent). */
  consents: z.object({
    transactionalSms: z.boolean(),
    marketing: z.boolean(),
    disclosureVersion: z.string().max(40).optional(),
  }),
  attribution: AttributionSchema.default({}),
  /** From a token link or a verified one-time code. */
  identityToken: z.string().min(20).max(128).optional(),
  /** Reference of a visit just booked, for a family member back-to-back (OB-20). */
  familyOf: z.string().max(16).optional(),
  /** Cloudflare Turnstile token when booking is protected. */
  turnstileToken: TurnstileTokenSchema,
});
export type SubmitRequest = z.infer<typeof SubmitRequestSchema>;

export const SubmitResultSchema = z.object({
  reference: z.string(),
  /** `requested`: staff approve first. `confirmed`: written or being written. */
  status: z.enum(['requested', 'confirmed']),
  start: InstantSchema,
  end: InstantSchema,
  typeLabel: z.string(),
  providerName: z.string(),
  officeName: z.string(),
  officePhone: z.string().nullable(),
  address: z.string(),
  message: z.string(),
  /** ICS calendar body for "Add to calendar". */
  ics: z.string(),
});
export type SubmitResult = z.infer<typeof SubmitResultSchema>;

/* ------------------------------------------------------------------------ */
/* Funnel progress, callback, identity                                       */
/* ------------------------------------------------------------------------ */

export const BOOKING_STEPS = ['type', 'when', 'details', 'done'] as const;
export const BookingStepSchema = z.enum(BOOKING_STEPS);
export type BookingStep = z.infer<typeof BookingStepSchema>;

export const ProgressSchema = z.object({
  visitorId: VisitorIdSchema,
  step: BookingStepSchema,
  typeId: z.uuid().optional(),
  slotStart: InstantSchema.optional(),
  mobile: z.string().max(40).optional(),
  firstName: z.string().max(60).optional(),
  transactionalConsent: z.boolean().default(false),
  attribution: AttributionSchema.default({}),
});
export type Progress = z.infer<typeof ProgressSchema>;

export const CallbackRequestSchema = z.object({
  visitorId: VisitorIdSchema,
  typeId: z.uuid().optional(),
  firstName: NameSchema,
  mobile: MobileSchema,
  reason: z.string().max(300).optional(),
  transactionalConsent: z.boolean(),
  attribution: AttributionSchema.default({}),
  turnstileToken: TurnstileTokenSchema,
});
export type CallbackRequest = z.infer<typeof CallbackRequestSchema>;

export const IdentifyRequestSchema = z.object({
  firstName: NameSchema,
  lastName: NameSchema,
  birthdate: LocalDateSchema,
  turnstileToken: TurnstileTokenSchema,
});
export type IdentifyRequest = z.infer<typeof IdentifyRequestSchema>;

/** Identical in shape and wording whether or not a patient matched (OB-08). */
export const IdentifyChallengeSchema = z.object({
  challengeId: z.uuid(),
  destination: z.string(),
  message: z.string(),
});
export type IdentifyChallenge = z.infer<typeof IdentifyChallengeSchema>;

export const VerifyCodeSchema = z.object({
  challengeId: z.uuid(),
  code: z.string().regex(/^\d{6}$/),
});

export const IdentityTokenSchema = z.object({ identityToken: z.string() });

/* ------------------------------------------------------------------------ */
/* Staff: the Online Requests queue                                          */
/* ------------------------------------------------------------------------ */

export const QUEUE_STATUSES = [
  'requested',
  'needs_match',
  'writing',
  'awaiting_entry',
  'written',
  'write_failed',
  'expired',
  'rejected',
] as const;
export const QueueStatusSchema = z.enum(QUEUE_STATUSES);
export type QueueStatus = z.infer<typeof QueueStatusSchema>;

export const QUEUE_STATUS_LABEL: Record<QueueStatus, string> = {
  requested: 'Requested',
  needs_match: 'Needs match',
  writing: 'Writing to PMS',
  awaiting_entry: 'Enter in PMS',
  written: 'Written to PMS',
  write_failed: 'Write failed',
  expired: 'Expired',
  rejected: 'Rejected',
};

export const MatchFieldSchema = z.enum(['firstName', 'lastName', 'birthdate', 'mobile', 'email']);
export type MatchField = z.infer<typeof MatchFieldSchema>;

export const MatchCandidateSchema = z.object({
  patientId: z.uuid(),
  firstName: z.string(),
  lastName: z.string(),
  preferredName: z.string().nullable(),
  birthdate: LocalDateSchema.nullable(),
  mobile: z.string().nullable(),
  email: z.string().nullable(),
  chartNumber: z.string().nullable(),
  /** 0..100. */
  score: z.number().int().min(0).max(100),
  /** Fields that agree exactly; the UI highlights the rest. */
  agrees: z.array(MatchFieldSchema),
});
export type MatchCandidate = z.infer<typeof MatchCandidateSchema>;

export const QueueRowSchema = z.object({
  id: z.uuid(),
  reference: z.string(),
  locationId: z.uuid(),
  officeName: z.string(),
  status: QueueStatusSchema,
  start: InstantSchema,
  end: InstantSchema,
  typeId: z.uuid().nullable(),
  typeName: z.string(),
  typeColorToken: z.string(),
  providerId: z.uuid().nullable(),
  providerName: z.string(),
  operatoryName: z.string().nullable(),
  bookingFor: BookingForSchema,
  patient: z.object({
    firstName: z.string(),
    lastName: z.string(),
    birthdate: LocalDateSchema.nullable(),
    mobile: z.string().nullable(),
    email: z.string().nullable(),
  }),
  booker: z.object({ firstName: z.string(), lastName: z.string(), mobile: z.string() }).nullable(),
  /** Linked Velora patient (existing, or chosen in the match resolver). */
  patientId: z.uuid().nullable(),
  isNewPatient: z.boolean(),
  candidates: z.array(MatchCandidateSchema),
  insurance: z.string().nullable(),
  note: z.string().nullable(),
  consents: z.object({ transactionalSms: z.boolean(), marketing: z.boolean() }),
  source: z.string(),
  attribution: AttributionSchema,
  createdAt: InstantSchema,
  slaDueAt: InstantSchema,
  writePath: BookingWritePathSchema,
  writeReason: z.string().nullable(),
  writeError: z.string().nullable(),
  appointmentId: z.uuid().nullable(),
  commandStatus: z.string().nullable(),
  /** The patient confirmation text: who would be told what, and whether it went. */
  notice: z.object({
    state: z.enum(['not_yet', 'held_sms_disabled', 'sent', 'no_consent']),
    detail: z.string(),
  }),
  creator: z.string(),
  decidedBy: z.string().nullable(),
  decidedAt: InstantSchema.nullable(),
  rejectReason: z.string().nullable(),
  familyOf: z.string().nullable(),
});
export type QueueRow = z.infer<typeof QueueRowSchema>;

export const QueueQuerySchema = z.object({
  view: z.enum(['open', 'done', 'all']).default('open'),
});

export const QueueSchema = z.object({
  rows: z.array(QueueRowSchema),
  counts: z.object({
    open: z.number().int(),
    needsMatch: z.number().int(),
    writeFailed: z.number().int(),
    late: z.number().int(),
    leads: z.number().int(),
  }),
  generatedAt: InstantSchema,
});
export type Queue = z.infer<typeof QueueSchema>;

export const REJECT_REASONS = [
  'Time no longer available',
  'Needs a different visit type',
  'Please call the office',
  'Duplicate request',
  'Other',
] as const;

export const RejectSchema = z.object({ reason: z.string().min(1).max(200) });
export const LinkSchema = z.object({ patientId: z.uuid() });

/* ------------------------------------------------------------------------ */
/* Staff: explainer, links, leads, funnel                                    */
/* ------------------------------------------------------------------------ */

export const ExplainQuerySchema = z.object({
  typeId: z.uuid(),
  operatoryId: z.uuid(),
  start: InstantSchema,
});

export const ExplainRuleSchema = z.object({
  id: z.string(),
  label: z.string(),
  ok: z.boolean(),
  detail: z.string(),
  /** Where to fix it: a settings anchor inside the booking module. */
  fix: z.string().nullable(),
});
export type ExplainRule = z.infer<typeof ExplainRuleSchema>;

export const ExplainSchema = z.object({
  bookable: z.boolean(),
  start: InstantSchema,
  end: InstantSchema,
  rules: z.array(ExplainRuleSchema),
});
export type Explain = z.infer<typeof ExplainSchema>;

export const CreateLinkSchema = z.object({
  patientId: z.uuid(),
  typeId: z.uuid().optional(),
});
export const BookingLinkSchema = z.object({
  url: z.string(),
  expiresAt: InstantSchema,
});
export type BookingLink = z.infer<typeof BookingLinkSchema>;

export const LeadRowSchema = z.object({
  id: z.uuid(),
  createdAt: InstantSchema,
  kind: z.enum(['abandoned', 'callback', 'emergency', 'must_call']),
  firstName: z.string().nullable(),
  mobile: z.string().nullable(),
  typeName: z.string().nullable(),
  stepReached: z.string().nullable(),
  slotViewed: InstantSchema.nullable(),
  source: z.string(),
  campaign: z.string().nullable(),
  consent: z.boolean(),
  followUp: z.string(),
  status: z.string(),
});
export type LeadRow = z.infer<typeof LeadRowSchema>;

export const FunnelQuerySchema = z.object({
  from: LocalDateSchema,
  to: LocalDateSchema,
});

export const FunnelSchema = z.object({
  from: LocalDateSchema,
  to: LocalDateSchema,
  steps: z.array(z.object({ step: z.string(), label: z.string(), count: z.number().int() })),
  bySource: z.array(
    z.object({
      source: z.string(),
      campaign: z.string().nullable(),
      visits: z.number().int(),
      requests: z.number().int(),
      written: z.number().int(),
    }),
  ),
  byOffice: z.array(
    z.object({
      locationId: z.uuid(),
      officeName: z.string(),
      visits: z.number().int(),
      requests: z.number().int(),
      written: z.number().int(),
    }),
  ),
});
export type Funnel = z.infer<typeof FunnelSchema>;
